import React, { useEffect } from 'react';
import { Text } from '@fluentui/react/lib/Text';
import { Icon } from '@fluentui/react';
import TimeTaxSupportSystemClient from '../../../system';
import MessageCourier from '../../../lib/MessageCourier';
import { Collaborator } from '../../../system/collaborators/Collaborator';
import CollaboratorTeam from '../../../system/collaborators/collaborator_team/CollaboratorTeam';
import CollaboratorPersona from '../../Collaborator/CollaboratorPersona';
import ProjectConfigurationTeamForm from './ProjectConfigurationTeamForm';
import CustomInputIfc from '../../../lib/FormValidator/Form/CustomInputIfc';
import InputField from '../../../lib/FormValidator/Form/InputField';
import ProjectConfigurationForm from './ProjectConfigurationForm';
import ComboBoxInput from '../../../lib/FormValidator/Form/ComboBoxInput';
import ButtonInput from '../../../lib/FormValidator/Form/ButtonInput';
import ErrorListForForm from '../../../lib/FormValidator/Form/ErrorListForForm';
import { useTranslation } from 'react-i18next';
import { SHOW_HORAS_ESTIMADAS } from '../../../common/constants';
import ToggleInput from '../../../lib/FormValidator/Form/ToggleInput';
import { OrganizationChartDropdown } from '../../Common/OrganizationChartDropdown';
import { OrganizationChartNode } from '../../../system/organization_chart/OrganizationChartNode';
import { useCustomInput } from '../../../common/custom-hooks/useCustomInput';
import useAsyncDataCourier from '../../../common/custom-hooks/useAsyncDataCourier';
import { User } from '../../../system/User';

interface IProjectConfigurationTeam {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  collaboratorsList: Collaborator[];
  formProjectTeam: ProjectConfigurationTeamForm;
  formProject: ProjectConfigurationForm;
  onBlurCustomInput: (customInput: CustomInputIfc) => void;
  projectTeam: CollaboratorTeam[];
  onChangeProjectTeam: (team: CollaboratorTeam[]) => void;
  organizationChartNodes: OrganizationChartNode[];
  setShowDialogPreloadTeam: (show: boolean) => void;
  collaboratorsInSelectedNode: Collaborator[];
  setCollaboratorsInSelectedNode: (collaborators: Collaborator[]) => void;
  blockui:(block: boolean) => void;
  user:User;
}

export const ProjectConfigurationTeam = (props: IProjectConfigurationTeam) => {
  const { t } = useTranslation();

  const [collaboratorOptionsFiltered, setColaboratorOptionsFiltered] = React.useState<Collaborator[]>(
      props.collaboratorsList
  );

  useEffect(() => {
    // Se obtiene un array de los IDs de los colaboradores agregados
     const projectTeamCollaboratorIds = props.projectTeam.map(
       (teamMember) => teamMember.getCollaborator().getId()
     );

     // Se filtran los que ya esten selecionado
      setColaboratorOptionsFiltered((prev) => {
      return prev.filter((option) => !projectTeamCollaboratorIds.includes(option.getId()));
    });
  }, [props.projectTeam])

  const node = useAsyncDataCourier(
    () => {
      if (props.user) {
        return props.system.getOrganizationChartSystem().getAvailableNodesOf(props.user);
      }
      return Promise.resolve([]);
    },
    [props.system, props.user],
    props.courier
);

  const onChangeCollaborator = (customInput: CustomInputIfc) => {
    props.onChangeProjectTeam(
        props.formProjectTeam
            .getInputProjectTeamItems()
            .getValue()
            .filter((col) => !col.get__delete())
    );

    setCollaboratorOutsideTeam();
  };

  const collaboratorOptions = React.useMemo(() => {
    return props.collaboratorsList;
  }, [props.collaboratorsList]);

  React.useEffect(() => {
    setCollaboratorOutsideTeam();
  }, [props.collaboratorsList]);

  const setCollaboratorOutsideTeam = (val?: string) => {
    let _projectTeam = props.formProjectTeam.getInputProjectTeamItems().getValue();
    let ret: Collaborator[] = collaboratorOptions.filter((col) => {
      const colIsInTeam = _projectTeam.some(
          (colTeam) => colTeam.collaboratorIs(col) && !colTeam.get__delete()
      );
      return !colIsInTeam && col.fullNameLike(val);
    });

    setColaboratorOptionsFiltered(ret);
  };

  const onDeleteItem = (collaboratorTeam: CollaboratorTeam) => {
    const currentResponsible = props.formProjectTeam.getCollaboratorResponsibleValue();
     if (currentResponsible && collaboratorTeam.collaboratorIs(currentResponsible)) {
       props.formProjectTeam.removeResponsibleByCollaborator(collaboratorTeam);
       setCollaboratorResponsible(null)
      }
    props.formProjectTeam.getInputProjectTeamItems().delete(collaboratorTeam);
    onChangeCollaborator(props.formProjectTeam.getInputProjectTeamItems());
    setCollaboratorOutsideTeam();
  };

  const [collaboratorResponsible, setCollaboratorResponsible] = React.useState<Collaborator | null>(null);

  React.useEffect(() => {
    setCollaboratorResponsible(
        props.formProject.getProjectTeamForm().getCollaboratorResponsibleInput().getValue()
    );
  }, [props.formProject]);

  const onChangeCollaboratorResponsible = (_collaboratorResponsible: Collaborator) => {
    setCollaboratorResponsible(_collaboratorResponsible);
  };

  const onBlurPercentageAssignation = async (customInput: CustomInputIfc, colTeam: CollaboratorTeam) => {
    props.onBlurCustomInput(customInput);

    const collaboratorForm = props.formProjectTeam.getInputProjectTeamItems().getFormFor(colTeam);
    if (collaboratorForm.isValid()) {
      const percentage = +customInput.getValue();

      const hoursAssignated = await props.system
          .getProjectSystem()
          .calculatePercentageAssignationHours(
              props.formProject.getStartValue().toJSDate(),
              props.formProject.getEndValue().toJSDate(),
              colTeam.getCollaborator().getId(),
              percentage
          );

      colTeam.setPercentage_asignation(percentage);
      colTeam.setTotal_hours_asignation(hoursAssignated.horasAsignadas);
    }

    onChangeCollaborator(customInput);
  };

  const onChangeResponsible = (customInput: CustomInputIfc, colTeam: CollaboratorTeam) => {
    props.onChangeProjectTeam(props.formProjectTeam.setResponsibleFor(colTeam));
    onChangeCollaboratorResponsible(colTeam.getCollaborator());
  };

  const isResponsible = (colTeam: CollaboratorTeam): boolean => {
    return !!collaboratorResponsible && colTeam.collaboratorIs(collaboratorResponsible);
  };

  const nodeInput = useCustomInput(() => {
      return props.formProject.getOrganizationEquipoInput();
    }, [props.formProject]);

  const getCollaboratorsInSelectedNode = React.useCallback(async () => {
      const node = nodeInput.getValue();
      if (!node) {
        return;
      }
      try {
        props.blockui(true);
        const collaboratorList = await props.system.getListCollaboratorForJefatura(node.id);
        const collaboratorSystem = props.system.getCollaboratorSystem();
       props.blockui(false);
        const parsedCollaborators = collaboratorList.map(item => {
          const {
            id,
            name,
            lastName,
            email,
            birthDay,
            initials,
            active,
            cargoColaborador
          } = item;
    
          const collaboratorData = {
            col_activo: active,
            col_apellido: lastName,
            col_id_sap: '',
            col_imagen_url: '',
            col_email: email,
            col_fecha_nacimiento: birthDay,
            col_id: id,
            col_iniciales: initials,
            col_nombre: name,
            cargoColaborador: cargoColaborador && {
              carcol_activo: cargoColaborador.carcol_activo,
              carcol_cargoid: cargoColaborador.cargo.cargo_id,
              carcol_colid: cargoColaborador.carcol_colid,
              carcol_es_actual: cargoColaborador.carcol_es_actual,
              carcol_fecha_desde: cargoColaborador.carcol_fecha_desde,
              carcol_fecha_hasta: cargoColaborador.carcol_fecha_hasta,
              carcol_id: cargoColaborador.carcol_id,
              carcol_norid: cargoColaborador.carcol_norid,
              cargo: cargoColaborador.cargo && {
                cargo_activo: cargoColaborador.cargo.cargo_activo,
                cargo_id: cargoColaborador.cargo.cargo_id,
                cargo_nombre: cargoColaborador.cargo.cargo_nombre
              }
            }
          };

          return Collaborator.fromICollaborator(collaboratorSystem, collaboratorData);
        });
       
        props.setCollaboratorsInSelectedNode(parsedCollaborators);
      } catch (error) {
        console.error("Error al obtener colaboradores:", error);
      }
    }, [nodeInput.getValue()]);

    React.useEffect(() => {
      getCollaboratorsInSelectedNode();
    }, [getCollaboratorsInSelectedNode]);


  return (
      <div className="ms-Grid bg-white-h100 ms-depth-4" dir="ltr">
        <div className="ms-Grid-row padding-1">
          <div className="ms-Grid-col ms-sm12">
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-sm6 ">
                <Text variant={'large'} className={'project-config-section-title'}>
                  <Icon iconName="Group" className="text-icon" />
                  {t('Equipo (*)')}
                </Text>
              </div>
              <div className="ms-Grid-row mt-2">
                <div className="ms-Grid-col ms-sm12">
                  <div className="ms-Grid-col ms-sm12">
                    <ErrorListForForm customForm={props.formProjectTeam} />
                    {/* <ErrorListForList customList={props.formProjectTeam.getInputProjectTeamItems()} /> */}
                  </div>
                </div>
              </div>
            </div>

            <div className="ms-Grid-row mt-2">
              <div className="ms-Grid-col ms-sm12">
                <ToggleInput
                    input={props.formProject.getSuggestTagInput()}
                    onBlur={props.onBlurCustomInput}
                    offText={'No taggear participantes automaticamente al crear actividades'}
                    onText={'Taggear participantes automaticamente al crear actividades'}
                />
              </div>
            </div>
            <div className="ms-Grid-row mt-2">
{node?        <div className="ms-Grid-col ms-sm6 ">
                <OrganizationChartDropdown
                    // label={lastLevelNode ? lastLevelNode.getName() : ''}
                    label={t('Equipo por jefatura')}
                    options={node}
                    autoComplete={'off'}
                    onChange={(node) => {
                      const input = nodeInput;
                      input.setValue(node);
                      input.onBlur();
                      props.onBlurCustomInput(input);
                    }}
                    selectedKey={
                      props.formProject.getOrganizationEquipoValue()
                          ? props.formProject.getOrganizationEquipoValue()!.getId()
                          : undefined
                    }
                    hasError={props.formProject.getOrganizationEquipoInput()}
                />
              </div>:null}
            </div>

            <div className="ms-Grid-row mt-2 pb-30px">
              <div className="ms-Grid-col ms-sm6 ">
                {collaboratorOptionsFiltered && (
                    <>
                      <ComboBoxInput
                          label="Colaboradores"
                          projectTeamCollaboratorIds={props.projectTeam.map(p=> p.getCollaborator().getId())}
                          colaboratorOptions={collaboratorOptions}
                          colaboratorOptionsFiltered={collaboratorOptionsFiltered}
                          setColaboratorOptionsFiltered={setColaboratorOptionsFiltered}
                          onSelect={(key) => {
                            let col = props.collaboratorsList.find((item) => item.isIdentifiedBy(String(key)));
                            if (col) {
                              const colaborador = CollaboratorTeam.initializeFromColaborador(col);
                              props.formProjectTeam.getInputProjectTeamItems().setValue(colaborador);
                              onChangeCollaborator(props.formProjectTeam.getInputProjectTeamItems());
                            }
                          }}
                      ></ComboBoxInput>
                    </>
                )}
              </div>
            </div>
            {props.projectTeam.map((colTeam, i) => {
              if(!colTeam.getCollaborator().getActive()){
                return null
              }
              return (
                  <div key={i} className="ms-Grid-row mt-2">
                    <div className="ms-Grid-col ms-sm6 ">
                      <div>
                        <CollaboratorPersona collaborator={colTeam.getCollaborator()} />
                      </div>
                    </div>
                    {props.formProjectTeam.getInputProjectTeamItems().getFormFor(colTeam) && (
                        <div className="ms-Grid-col ms-sm1 text-center">
                          {
                            <ButtonInput
                                checked={isResponsible(colTeam)}
                                onBlur={props.onBlurCustomInput}
                                input={props.formProjectTeam
                                    .getInputProjectTeamItems()
                                    .getFormFor(colTeam)
                                    .getResponsibleInput()}
                                offIcon={'UserFollowed'}
                                onIcon={'PartyLeader'}
                                onChangeExtended={(ci) => onChangeResponsible(ci, colTeam)}
                            />
                          }
                        </div>
                    )}
                    {SHOW_HORAS_ESTIMADAS ? (
                        <>
                          {props.formProjectTeam.getInputProjectTeamItems().getFormFor(colTeam) && (
                              <div className="ms-Grid-col ms-sm2 px-0">
                                <InputField
                                    input={props.formProjectTeam
                                        .getInputProjectTeamItems()
                                        .getFormFor(colTeam)
                                        .getPercentageAssignationInput()}
                                    label=""
                                    onBlur={(customInput) => onBlurPercentageAssignation(customInput, colTeam)}
                                    propsInput={{
                                      placeholder: 'Asig.',
                                      suffix: '%',
                                      maxLength: 5,
                                      title: 'Porcentaje de asignación de trabajo',
                                    }}
                                />
                              </div>
                          )}
                          <div className="ms-Grid-col ms-sm2 text-center mt-1">
                            <Text variant={'small'} className="text-overflow" title="Estimado de horas a cargar">
                              {colTeam.getTotal_hours_asignation()} Hs.
                            </Text>
                          </div>
                        </>
                    ) : (
                        <>
                          <div className="ms-Grid-col ms-sm2 px-0"></div>
                          <div className="ms-Grid-col ms-sm2 text-center mt-1"></div>
                        </>
                    )}

                    <div className="ms-Grid-col ms-sm1 mt-1">
                      <Icon
                          iconName="Trash"
                          className="cursor-pointer"
                          title={t('Eliminar del equipo')}
                          onClick={() => onDeleteItem(colTeam)}
                      />
                    </div>
                  </div>
              );
            })}
          </div>
        </div>
        <br/>
      </div>
  );
};
